import { useQuery } from "@tanstack/react-query";
import actualPerformanceService, {
  type ActualPerformanceResponse,
} from "../services/actualPerformanceService";
import type { FactorEntry } from "../services/productFactorsService";
import {
  computeCumulativePerformance,
  getV3RangeForFilter,
  type V3TimeFilter,
} from "../components/performanceChartV3Utils";
import type { StrategyPerfMetrics } from "./useStrategyData";

export const actualPerformanceQueryKeys = {
  self: (authenticatedUserId: string) =>
    ["actual-performance", "self", authenticatedUserId] as const,
  forUser: (userId: string) =>
    ["actual-performance", "admin", userId] as const,
};

export function useActualPerformance(
  userId?: string,
  enabled = true,
  authenticatedUserId?: string
) {
  return useQuery<ActualPerformanceResponse, Error>({
    queryKey: userId
      ? actualPerformanceQueryKeys.forUser(userId)
      : actualPerformanceQueryKeys.self(authenticatedUserId ?? ""),
    queryFn: ({ signal }) =>
      userId
        ? actualPerformanceService.getActualPerformanceForUser(userId, signal)
        : actualPerformanceService.getActualPerformance(signal),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && (!!userId || !!authenticatedUserId),
  });
}

export function actualItemsToFactors(
  items: Array<{ date: string; twr: number | null }>
): FactorEntry[] {
  return items
    .filter((item): item is { date: string; twr: number } => item.twr != null)
    .map((item) => ({
      t: item.date.includes("T") ? item.date : `${item.date}T00:00:00.000Z`,
      f: 1 + item.twr / 100,
    }));
}

const toMetricPoints = (factors: FactorEntry[]) =>
  factors.map((f) => ({ metricDate: f.t, metricValue: f.f }));

/**
 * Cumulative time-weighted return for a single period, computed the same way
 * the strategy metrics (useStrategyData) and the performance chart do: the
 * window is anchored to the most recent factor (not wall-clock today), and
 * "All"/"Since inception" include every factor. Both the dashboard widget and
 * the modal table call this so their numbers can never diverge.
 *
 * `sortedFactors` must be ascending by date.
 */
export function computeActualPeriodReturn(
  sortedFactors: FactorEntry[],
  period: V3TimeFilter
): number | null {
  if (sortedFactors.length === 0) return null;

  if (period === "All" || period === "Since inception") {
    return computeCumulativePerformance(toMetricPoints(sortedFactors));
  }

  // Actual performance is a UTC date-only daily series, so "Today" means the
  // factor whose UTC date matches today's UTC date.
  if (period === "Today") {
    const now = new Date();
    const todayMs = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );
    const filtered = sortedFactors.filter(
      (f) => new Date(f.t).getTime() >= todayMs
    );
    return computeCumulativePerformance(toMetricPoints(filtered));
  }

  const anchor = new Date(sortedFactors[sortedFactors.length - 1].t);
  const inceptionDate = sortedFactors[0].t;
  const range = getV3RangeForFilter(period, anchor, inceptionDate);
  const fromMs = range.from
    ? new Date(range.from).getTime()
    : Number.NEGATIVE_INFINITY;
  const toMs = range.to ? new Date(range.to).getTime() : Number.POSITIVE_INFINITY;
  const filtered = sortedFactors.filter((f) => {
    const ms = new Date(f.t).getTime();
    return ms > fromMs && ms <= toMs;
  });
  return computeCumulativePerformance(toMetricPoints(filtered));
}

export function computeActualPerfMetrics(
  factors: FactorEntry[]
): StrategyPerfMetrics | null {
  if (factors.length === 0) return null;

  const sorted = [...factors].sort(
    (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime()
  );

  return {
    inception: computeActualPeriodReturn(sorted, "Since inception"),
    returnToday: computeActualPeriodReturn(sorted, "Today"),
    return1D: computeActualPeriodReturn(sorted, "1 day"),
    return1M: computeActualPeriodReturn(sorted, "1 month"),
    return3M: computeActualPeriodReturn(sorted, "3 months"),
  };
}
